<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, watch, onMounted, useRouter, onUnmounted } from "#imports";
import { ClientOnly } from "#components";
import { useAuthState } from "~/composables/useAuthState";
import PointsHistoryPopup from "~/components/PointsHistoryPopup.vue";


const { t } = useI18n();
const showSettings = ref(false);

// 使用新的认证状态composable
const {
  user,
  isAuthenticated,
  isLoggingIn,
  handleSignIn,
  totalPoints,
  isRefreshing,
  handleLogout,
} = useAuthState();

// 积分历史弹窗
const showPointsHistoryPopup = ref(false);

// 处理用户名过长中间省略号
const truncateMiddle = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  const partLength = Math.floor((maxLength - 3) / 2);
  return text.substr(0, partLength) + "..." + text.substr(-partLength);
};

// 用户信息
const userInfo = ref<any>({
  avatar: "", // 默认头像
  name: "",
  email: "", // 添加邮箱字段
});

// 更新用户信息
const updateUserInfo = () => {
  if (user.value) {
    userInfo.value.name = user.value.firstName || t("user.role");
    userInfo.value.avatar =
      user.value.imageUrl || "/assets/svg/ic-sharp-person.svg";
    userInfo.value.email =
      user.value.emailAddresses?.[0]?.emailAddress || t("user.noEmail");
  }
};

// 监听用户信息变化，仅处理UI更新
watch(
  user,
  () => {
    if (user.value) {
      updateUserInfo();
    }
  },
  { deep: true }
);

// 监听认证状态变化，仅处理UI更新
watch(isAuthenticated, (newValue: any) => {
  if (newValue) {
    updateUserInfo();
  }
});

const navItems = [
  { icon: "i-carbon-home", key: "nav.home", route: "/" },
  { 
    icon: "i-carbon-edit", 
    key: "nav.create", 
    route: "/create",
    children: [
      {
        icon: "i-carbon-music",
        key: "nav.createMusic",
        route: "/create"
      },
      {
        icon: "i-carbon-airline-rapid-board",
        key: "nav.rapLyric",
        route: "/rap-lyrics-generator"
      },
      {
        icon: "i-carbon-document",
        key: "nav.lyricGenerator",
        route: "/lyrics-generator"
      },
      {
        icon: "i-carbon-user-profile",
        key: "nav.rapnameGenerator",
        route: "/rap-name-generator"
      },
      {
        icon: "i-carbon-split",
        key: "nav.vocalSeparation",
        route: "/vocal-separation"
      },
      {
        icon: "i-carbon-video",
        key: "nav.mvGenerator",
        route: "/mv-generator"
      }
    ]
  },
  { icon: "i-carbon-music", key: "nav.library", route: "/library" },
  {
    icon: "i-carbon-favorite",
    key: "nav.subscription",
    route: "/subscription",
  },
];

const router = useRouter();
const isMobile = ref(false);
const showCreateMenu = ref(true);

// 检查是否移动端
const checkMobile = () => {
  if (typeof window !== 'undefined') {
    isMobile.value = window.innerWidth < 768;
  }
};

// 检查当前路由是否为创作相关路由
const checkIfCreateRoute = () => {
  const path = router.currentRoute.value.path;
  const createRoutes = ['/create', '/lyrics-generator', '/rap-lyrics-generator', '/rap-name-generator', '/mv-generator'];
  
  // 如果当前路由是创作相关路由，则显示创作子菜单
  if (createRoutes.includes(path)) {
    showCreateMenu.value = true;
  }
};

// 监听页面大小和路由变化
onMounted(() => {
  try {
    // 检测移动端
    if (typeof window !== 'undefined') {
      checkMobile();
      window.addEventListener('resize', checkMobile);
    }
    
    // 初始检查当前路由是否创作相关
    checkIfCreateRoute();
  } catch (error) {
    console.error('AppNavigation挂载失败:', error);
  }
});

onUnmounted(() => {
  try {
    console.log('AppNavigation: 组件开始卸载');
    
    // 移除全局事件监听器
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', checkMobile);
    }
    
    // 确保菜单和弹窗都已关闭
    showSettings.value = false;
    showCreateMenu.value = false;
    showPointsHistoryPopup.value = false;
    
    console.log('AppNavigation: 组件卸载完成');
  } catch (error) {
    console.error('AppNavigation卸载失败:', error);
  }
});

// 处理导航点击
const handleNavClick = (item: any, event: MouseEvent) => {
  event.stopPropagation(); // 阻止事件冒泡
  if (item.key === 'nav.create') {
    if (isMobile.value) {
      // 移动端直接跳转到创作页面
      nextTick(() => {
        router.push('/m-create');
      });
    } else {
      // 桌面端显示子菜单
      showCreateMenu.value = !showCreateMenu.value;
    }
  } else {
    // showCreateMenu.value = false;
    nextTick(() => {
      router.push(item.route);
    });
  }
};

const toggleSettings = () => {
  showSettings.value = !showSettings.value;
};

// 处理本地登出UI
const handleLocalLogout = async () => {
  await handleLogout();
  showSettings.value = false;
};

// 打开积分历史弹窗
const openPointsHistoryPopup = () => {
  showPointsHistoryPopup.value = true;
  showSettings.value = false; // 关闭设置菜单
};
</script>

<template>
  <nav class="nav-container">
    <div
      class="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-purple-500/20 to-transparent hidden lg:block"
    ></div>
    <div class="nav-content">
      <!-- 固定的logo部分 -->
      <div class="nav-header-fixed">
        <div class="nav-header">
          <div class="logo-container">
            <img
              src="/images/logo.png"
              alt="MusicMint"
              class="w-6 h-6"
            />
            <span class="logo-text">MusicMint</span>
          </div>
        </div>
      </div>
      
      <!-- 可滚动的内容部分 -->
      <div class="nav-scrollable-content">
        <div class="nav-top">
          <div class="nav-items">
            <div
              v-for="item in navItems"
              class="cursor-pointer relative"
              :key="item.key"
              :class="[
                'nav-item',
                { active: router.currentRoute.value.path === item.route },
              ]"
              @click="(e) => handleNavClick(item, e)"
            >
              <div class="flex items-center w-full">
                <div :class="[item.icon, 'nav-icon']" />
                <span class="nav-label">{{ t(item.key) }}</span>
                <!-- 只在创作菜单项显示箭头，且只在PC端显示 -->
                <div v-if="item.key === 'nav.create'" class="ml-auto hidden lg:block">
                  <div 
                    :class="[
                      'i-carbon-chevron-down transition-transform duration-200',
                      { 'rotate-180': showCreateMenu }
                    ]"
                  />
                </div>
              </div>
              
              <!-- 创作子菜单 -->
              <div 
                v-if="item.key === 'nav.create' && showCreateMenu && !isMobile"
                class="create-menu"
              >
                <div
                  v-for="child in item.children"
                  :key="child.key"
                  class="create-menu-item"
                  :class="{ active: router.currentRoute.value.path === child.route }"
                  @click.stop="router.push(child.route)"
                >
                  <div :class="[child.icon, 'mr-2']" />
                  <span>{{ t(child.key) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="user-container">
          <UserSettingsPopup
            v-if="showSettings"
            @close="toggleSettings"
            @logout="handleLocalLogout"
          />

          <!-- 页脚链接 -->
          <div class="footer-links">
            <a
              href="/privacy.html"
              target="_blank"
              class="footer-link text-sm lg:px-4 lg:py-3 flex items-center justify-start"
            >
              <div class="i-carbon-document-security mr-2"></div>
              {{ t("footer.privacyPolicy") }}
            </a>
            <a
              href="/terms.html"
              target="_blank"
              class="footer-link text-sm py-3 lg:px-4 lg:py-3 flex items-center justify-start"
            >
              <div class="i-carbon-document-signed mr-2"></div>
              {{ t("footer.termsOfService") }}
            </a>
            <a
              href="#"
              class="footer-link text-sm py-3 lg:px-4 lg:py-3 flex items-center justify-start"
              @click.prevent="router.push('/about')"
            >
              <div class="i-carbon-information mr-2"></div>
              {{ t("footer.aboutUs") }}
            </a>
          </div>
          <div class="h-20 flex items-end justify-start">
            <ClientOnly>
              <div class="text-center w-full" v-if="!isAuthenticated">
                <div class="login-modal text-center px-5" >
                  <button
                    @click="handleSignIn"
                    class="login-button m-auto w-full text-center"
                    :disabled="isLoggingIn"
                  >
                    {{ t("user.login") }}
                    <span v-if="isLoggingIn" class="loading-spinner"></span>
                  </button>
                </div>
              </div>

              <!-- 已登录用户信息 -->
              <div class="box-border w-full" v-if="isAuthenticated">
                <div class="pl-4 user-info w-full" @click="toggleSettings">
                  <img
                    v-if="userInfo.avatar"
                    :src="userInfo.avatar"
                    :alt="userInfo.name"
                    class="user-avatar"
                  />
                  <div class="user-details">
                    <span class="user-name">{{ userInfo.name }}</span>
                    <span class="user-email">{{
                      truncateMiddle(userInfo.email, 17)
                    }}</span>
                  </div>
                </div>
                <!-- 用户积分信息 -->
                <div
                  class="pl-4 mt-2 text-sm ml-1 flex items-center cursor-pointer text-white hover:text-blue-500 dark:hover:text-blue-400"
                  @click="openPointsHistoryPopup"
                >
                  <span class="flex items-center">
                    {{ t("user.points") }}: 
                    <span :class="['ml-1', { 'animate-pulse': isRefreshing }]">
                      {{ totalPoints }}
                    </span>
                  </span>
                  <span
                    class="cursor-pointer pl-4 underline text-blue-500 dark:text-blue-400"
                    >{{ t("common.view") }}</span
                  >
                </div>
              </div>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <!-- 积分历史弹窗 -->
  <PointsHistoryPopup v-model:visible="showPointsHistoryPopup" />

  <!-- 移动端底部导航 -->
  <div class="mobile-tab-bar">
    <div class="flex justify-around items-center h-16">
      <NuxtLink 
        v-for="item in navItems" 
        :key="item.key"
        :to="item.key === 'nav.create' ? '/m-create' : item.route"
        class="flex items-center justify-center w-full h-full"
        :class="[
          (item.key === 'nav.create' && router.currentRoute.value.path.includes('/m-create')) || 
          router.currentRoute.value.path === item.route
            ? 'text-purple-600 dark:text-purple-400' 
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        ]"
      >
        <span class="font-medium text-sm">{{ t(item.key) }}</span>
      </NuxtLink>
    </div>
  </div>
</template>
<style>
.cl-modalBackdrop {
  align-items: center !important;
}
</style>
<style scoped>
.nav-container {
  @apply fixed shadow-lg z-20;
  /* PC端样式 */
  @apply lg:!w-[250px] lg:left-0 lg:top-0 lg:bottom-0 lg:!h-screen;
  /* 移动端样式 - 隐藏移动端的主导航容器 */
  @apply fixed bottom-0 left-0 w-full h-[3.5rem] bg-white dark:bg-gray-900 hidden lg:block;
}

.nav-content {
  @apply w-full h-screen flex flex-col relative z-10;
  /* PC端样式调整 */
  @apply lg:py-6 lg:px-6;
  /* 移动端样式调整 */
  @apply p-2;
}

/* 固定的头部Logo区域 */
.nav-header-fixed {
  @apply w-full lg:block hidden flex-shrink-0;
  @apply lg:mb-6;
}

/* 可滚动内容区域 */
.nav-scrollable-content {
  @apply flex flex-col justify-between flex-grow;
  /* 添加PC端滚动支持但隐藏滚动条 */
  @apply lg:overflow-y-auto;
  /* 隐藏滚动条 - Webkit浏览器 */
  &::-webkit-scrollbar {
    @apply hidden;
  }
  /* Firefox浏览器 */
  scrollbar-width: none;
  /* IE和Edge浏览器 */
  -ms-overflow-style: none;
  /* 调整上下间距 */
  @apply lg:mt-2 lg:mb-0;
  /* 设置滚动区域高度 */
  max-height: calc(100vh - 100px);
}

.nav-top {
  @apply flex flex-col gap-8;
}

.nav-header {
  @apply w-full;
}

.logo-container {
  @apply flex items-center gap-3 lg:pl-4;
}

.logo-circle {
  @apply w-8 h-8 rounded-full bg-blue-500 text-white;
  @apply flex items-center justify-center text-xl font-bold;
}

.logo-text {
  @apply text-lg font-semibold text-gray-800 dark:text-white;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-weight: 600;
}

.nav-items {
  @apply flex;
  /* PC端垂直排列 */
  @apply lg:!flex-col lg:gap-2;
  /* 移动端水平排列 - 完全隐藏 */
  @apply hidden lg:flex;
}

.nav-item {
  @apply flex flex-col transition-all duration-200 relative;
  /* 移动端样式：垂直排列 */
  @apply flex-col items-center gap-1 p-2;
  /* PC端样式：水平排列 */
  @apply lg:flex-col lg:items-start lg:px-4 lg:py-3 lg:w-full;
  @apply text-gray-500 dark:text-gray-400;
}

.nav-item.active {
  @apply text-blue-600 dark:text-blue-400;
}

.nav-item.active::before {
  content: "";
  @apply absolute left-0 w-1 bg-blue-600 dark:bg-blue-400 rounded-r-md;
  @apply hidden lg:block;
  /* 确保选中条高度固定 */
  height: 24px;
  top: 12px; /* 固定到文字位置 */
}

.nav-item:hover {
  @apply text-gray-800 dark:text-gray-200;
}

.nav-icon {
  @apply text-2xl transition-colors duration-200;
  /* PC端样式 */
  @apply lg:mr-3;
}

.nav-label {
  @apply hidden font-medium whitespace-nowrap text-xs transition-colors duration-200;
  /* PC端样式 */
  @apply lg:text-sm lg:block;
}

.user-container {
  @apply relative;
  /* 移动端隐藏用户信息 */
  @apply lg:block hidden;
  /* 增加底部距离 */
  @apply lg:mb-10;
}

.login-button {
  @apply bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700;
  @apply text-white font-medium px-6 py-2.5 rounded-lg shadow-md;
  @apply transition-all duration-300 transform hover:scale-105;
  @apply flex items-center justify-center gap-2;
  @apply disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100;
  /* 确保只在大屏幕上显示背景 */
  @apply lg:bg-gradient-to-r;
}

.logout-button {
  @apply bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md mt-2;
}

.token-info {
  @apply bg-gray-100 dark:bg-gray-800 p-3 rounded-md mt-2 text-xs;
  @apply max-w-[180px] overflow-hidden text-ellipsis;
}

.user-info {
  @apply flex items-center gap-3 rounded-xl;
  @apply py-4 mt-0 mb-2;
  @apply hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer;
}

.user-avatar {
  @apply w-10 h-10 rounded-full ring-2 ring-blue-100 dark:ring-blue-900;
}

.user-details {
  @apply flex flex-col justify-center;
}

.user-name {
  @apply text-sm font-medium text-gray-800 dark:text-gray-200;
}

.user-email {
  @apply text-xs text-gray-500 dark:text-gray-400;
  /* 调整邮箱显示，保证省略号在中间 */
  @apply truncate max-w-[120px] text-ellipsis;
}

.user-role {
  @apply text-xs text-gray-500 dark:text-gray-400;
}

/* 语言选择器样式 */
.language-selector {
  @apply flex items-center gap-2 p-4 rounded-xl cursor-pointer;
  @apply hover:bg-gray-100 dark:hover:bg-gray-800;
  @apply text-sm text-gray-700 dark:text-gray-300;
  @apply mb-2;
}

.language-menu {
  @apply absolute bottom-full left-0 right-0 mb-2;
  @apply bg-white dark:bg-gray-800 rounded-xl shadow-lg;
  @apply p-4 z-30;
}

.language-menu-header {
  @apply flex justify-between items-center mb-3;
  @apply text-sm font-medium text-gray-700 dark:text-gray-300;
}

.close-button {
  @apply text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200;
}

.language-options {
  @apply flex flex-col gap-2;
}

.language-option {
  @apply px-3 py-2 rounded-lg text-left;
  @apply text-sm text-gray-700 dark:text-gray-300;
  @apply hover:bg-gray-100 dark:hover:bg-gray-700;
}

.language-option.active {
  @apply bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400;
}

.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}

/* 添加加载动画 */
.loading-spinner {
  @apply inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2;
}

/* 页脚链接样式 */
.footer-links {
  @apply flex flex-col gap-1 mb-4 px-2;
  @apply text-gray-500 dark:text-gray-400;
}

.footer-link {
  @apply hover:text-gray-700 dark:hover:text-gray-300 transition-colors;
}

/* 添加刷新动画 */
.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.create-menu {
  @apply w-full;
  @apply overflow-hidden;
  animation: slideDown 0.2s ease-in-out;
}

.create-menu-item {
  @apply px-4 pt-8 cursor-pointer flex items-center;
  @apply text-sm text-gray-500 dark:text-gray-400;
  @apply pl-6; /* 添加左侧缩进 */
}

.create-menu-item:hover {
  @apply text-gray-800 dark:text-gray-200;
}

/* 添加二级菜单选中样式 */
.create-menu-item.active {
  @apply text-blue-600 dark:text-blue-400;
  @apply bg-transparent;
}

/* 移除二级菜单的左侧边框 */
.create-menu-item.active::before {
  display: none;
}

/* 当创作菜单展开时，调整选中条的位置 */
.nav-item:has(.create-menu.active)::before {
  top: 12px; /* 保持与普通菜单项一致的位置 */
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 200px;
  }
}

/* 确保移动端不显示下拉菜单 */
@media (max-width: 1024px) {
  .create-menu {
    display: none;
  }
}

/* 移除之前的边框相关样式 */
.nav-item:has(.create-menu) {
  @apply border-b-0;
}

/* 完全独立的移动端底部导航 */
.mobile-tab-bar {
  @apply fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 z-30;
  @apply lg:hidden; /* 在大屏幕上隐藏 */
}
</style> 